import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@legal-500/legal500-design-system";
import { ROUTES } from "../../../../routes";
import styles from "./CmsLayout.module.css";

export type BreadcrumbItem = {
  label: string;
  path?: string;
  state?: Record<string, unknown>;
};

type CmsLayoutProps = {
  children: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  aside?: ReactNode;
  /** The aside is drawn over the page instead of beside it, so opening or closing it moves nothing. */
  asideFloats?: boolean;
};

export function CmsLayout({
  children,
  breadcrumbs = [],
  aside,
  asideFloats = false,
}: CmsLayoutProps) {
  const navigate = useNavigate();

  const items: BreadcrumbItem[] = [
    { label: "Research Portal", path: ROUTES.research.cms },
    ...breadcrumbs,
  ];

  const canGoBack = items.length > 1;
  const parentPath = canGoBack ? items[items.length - 2].path : null;

  const breadcrumbNav = canGoBack && (
    <nav className={styles.breadcrumb} aria-label="CMS navigation">
      <button
        type="button"
        className={styles.breadcrumbBack}
        onClick={() => {
          if (!parentPath) return;
          const parentItem = items[items.length - 2];
          navigate(parentPath, { state: parentItem.state });
        }}
        aria-label="Go back"
      >
        <Icon icon="chevron-left" size="small" />
      </button>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <span key={item.path ?? `bc-${index}`} className={styles.breadcrumbItem}>
            {index > 0 && (
              <span className={styles.breadcrumbSeparator} aria-hidden="true">
                /
              </span>
            )}
            {isLast ? (
              <span className={styles.breadcrumbCurrent} aria-current="page">
                {item.label}
              </span>
            ) : item.path ? (
              <Link to={item.path} state={item.state} className={styles.breadcrumbLink}>
                {item.label}
              </Link>
            ) : (
              <span className={styles.breadcrumbCurrent}>{item.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );

  if (asideFloats) {
    return (
      <div className={`${styles.wrapper} ${styles.wrapperWithAside}`}>
        <div className={styles.floatHost} data-float-host="">
          {aside && (
            <div className={styles.floatLayer}>
              <div className={styles.floatPanel}>{aside}</div>
            </div>
          )}
          {breadcrumbNav}
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={aside ? `${styles.wrapper} ${styles.wrapperWithAside}` : styles.wrapper}>
      {breadcrumbNav}
      {aside ? (
        <div className={styles.withAside}>
          <div className={styles.content}>{children}</div>
          <div className={styles.aside}>{aside}</div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}
